import * as React from "react"
import { Link as RLink, useNavigate } from "react-router-dom"
import {
  Flex,
  VStack,
  Icon,
  Avatar,
  AvatarBadge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Link,
  Tooltip,
  Box,
  Text,
  Divider,
  LinkProps,
} from "@chakra-ui/react"
import { RiDashboard3Line } from "react-icons/ri"
import { FiBell, FiSettings, FiLogOut } from "react-icons/fi"
import { RiShapeLine, RiGroupLine, RiGpsLine } from "react-icons/ri"
import { GrUpdate, GrMapLocation } from "react-icons/gr"
import { CgOrganisation } from "react-icons/cg"
import { useQueryClient } from "react-query"
import { ReactComponent as Logo } from "../assets/images/logo.svg"
import { AUTH_TOKEN, REFRESH_TOKEN, NAV_WIDTH } from "../lib/config"
import { useMe } from "./providers/MeProvider"
import { useFeatFlags } from "./providers/FeatFlagProvider"
import { OrgSwitcher } from "./OrgSwitcher"
import { useOrgs } from "./providers/OrgProvider"
import { useToast } from "../lib/hooks/useToast"
import {
  APPLE_DATASTUDIO_URL,
  GOOGLE_DATASTUDIO_URL,
  ORGS_HAS_DASHBOARD,
  ORGS_IS_APPLE,
  ORGS_IS_GOOGLE,
} from "../lib/static/organisation"
import { isSupervisor } from "../lib/helpers/role"

export const Nav = () => {
  const me = useMe()
  const client = useQueryClient()
  const toast = useToast()
  const { multi_org } = useFeatFlags()

  const { currentOrgs } = useOrgs()
  const navigate = useNavigate()
  const modalProps = useDisclosure()

  const handleLogout = () => {
    navigate("/")
    localStorage.removeItem(AUTH_TOKEN)
    localStorage.removeItem(REFRESH_TOKEN)
    client.setQueryData("me/", () => undefined)
    toast({ description: "Successfully logged out!" })
  }

  return (
    <Flex
      minH="-webkit-fill-available"
      justify="space-between"
      flexDirection="column"
      w={NAV_WIDTH}
      zIndex={50}
    >
      <VStack spacing={6} p={4} pt={8}>
        <Link to="/" as={RLink}>
          <Box as={Logo} w="40px" fill="gray.700" />
        </Link>
        {ORGS_IS_GOOGLE(currentOrgs) && !ORGS_IS_APPLE(currentOrgs) ? (
          <SidebarLink label="Dashboard" isExternal to={GOOGLE_DATASTUDIO_URL}>
            <Icon as={RiDashboard3Line} boxSize="20px" mr="1px" />
          </SidebarLink>
        ) : !ORGS_IS_GOOGLE(currentOrgs) && ORGS_IS_APPLE(currentOrgs) ? (
          <SidebarLink label="Dashboard" isExternal to={APPLE_DATASTUDIO_URL}>
            <Icon as={RiDashboard3Line} boxSize="20px" mr="1px" />
          </SidebarLink>
        ) : (
          ORGS_HAS_DASHBOARD(currentOrgs) && (
            <SidebarLink label="Dashboard" to="/dashboard">
              <Icon as={RiDashboard3Line} boxSize="20px" mr="1px" />
            </SidebarLink>
          )
        )}
        <SidebarLink label="Assets" to="/assets">
          <Icon as={GrMapLocation} boxSize="20px" />
        </SidebarLink>
        <SidebarLink label="Alerts" to="/alerts">
          <Icon as={FiBell} boxSize="20px" />
        </SidebarLink>
        <SidebarLink label="Zones" to="/zones">
          <Icon as={RiShapeLine} boxSize="20px" />
        </SidebarLink>
        <SidebarLink label="Trackers" to="/trackers">
          <Icon as={RiGpsLine} boxSize="20px" />
        </SidebarLink>
        {isSupervisor(me.roles) && (
          <SidebarLink label="Users" to="/users">
            <Icon as={RiGroupLine} boxSize="20px" />
          </SidebarLink>
        )}
      </VStack>
      <VStack spacing={4} px={4} pb={8}>
        <Menu placement="right-end">
          <Tooltip
            label={multi_org.visible ? "Organizations" : "Organization"}
            placement="auto"
            zIndex={50}
            hasArrow
          >
            <Box>
              <MenuButton>
                {currentOrgs.length > 1 ? (
                  <Avatar
                    bg="white"
                    icon={<Icon as={CgOrganisation} fontSize="1.25rem" color="gray.700" />}
                    size="sm"
                    borderWidth="1px"
                    borderColor="gray.700"
                    borderRadius={50}
                  >
                    <AvatarBadge
                      boxSize="1.25em"
                      bg="white"
                      borderWidth="1px"
                      borderColor="gray.300"
                      color="gray.700"
                      w={5}
                      h={5}
                      borderRadius={50}
                    >
                      {currentOrgs.length}
                    </AvatarBadge>
                  </Avatar>
                ) : (
                  <Avatar name={currentOrgs[0].name} src={currentOrgs[0].icon} size="sm" />
                )}
              </MenuButton>
            </Box>
          </Tooltip>
          <MenuList>
            <MenuItem onClick={modalProps.onOpen}>
              <Icon as={GrUpdate} boxSize="15px" mr={2} />
              Change {multi_org.visible ? "Organizations" : "Organization"}
            </MenuItem>
            <MenuItem>
              <Link to="/organisation-settings" as={RLink} w="100%" style={{ textDecorationLine: "none" }}>
                <Icon as={FiSettings} boxSize="15px" mr={2} />
                Settings
              </Link>
            </MenuItem>
          </MenuList>
        </Menu>
        <Menu placement="right-start">
          <Tooltip label="User" placement="auto" zIndex={50} hasArrow>
            <MenuButton>
              <Avatar name={me.display_name} src={me.avatar} size="sm" bg="green.500" objectFit="contain" />
            </MenuButton>
          </Tooltip>
          <MenuList py={0}>
            <Text pl={3} py={2} fontSize="0.8rem" fontWeight="600">
              {me.display_name}
            </Text>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <Icon as={FiLogOut} boxSize="15px" mr={2} />
              Logout
            </MenuItem>
          </MenuList>
        </Menu>
      </VStack>
      {modalProps.isOpen && <OrgSwitcher {...modalProps} />}
    </Flex>
  )
}

interface SLinkProps extends LinkProps {
  to: string
  label?: string
}

const SidebarLink = ({ label, to, ...props }: SLinkProps) => {
  const isActive = window.location.pathname === to
  return (
    <Tooltip label={label} placement="auto" zIndex={50} hasArrow>
      <Box
        boxSize="40px"
        borderRadius={12}
        _hover={{ bg: isActive ? "gray.200" : "gray.100" }}
        bg={isActive ? "gray.200" : undefined}
      >
        {props.isExternal ? (
          <Link display="flex" alignItems="center" justifyContent="center" h="100%" href={to} {...props}>
            {props.children}
          </Link>
        ) : (
          <Link
            as={RLink}
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="100%"
            to={to}
            {...props}
          >
            {props.children}
          </Link>
        )}
      </Box>
    </Tooltip>
  )
}
